import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function AdminPortalPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, login, activateSession, logout, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // If already logged in as admin, go straight to dashboard
  if (!authLoading && user && user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Clear any existing non-admin session
    if (user && user.role !== 'admin') {
      await logout();
    }
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      if (result.user?.role === 'admin') {
        activateSession(result.user);
        navigate('/admin');
      } else {
        setError('Access denied. Admin credentials required.');
      }
    } else {
      setError(result.error || 'Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="admin-portal">
      <div className="w-full max-w-md">
        {/* Logo / Branding */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
          <p className="text-sm text-gray-500 mt-1">WakzyAuto Tech Management</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#141414] rounded-2xl border border-white/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-400" data-testid="admin-portal-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-400">Email</label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@wakzyautotech.com"
                required
                className="bg-[#1a1a1a] border-white/10 text-white placeholder-gray-600 rounded-xl h-11 focus:border-blue-500"
                data-testid="admin-portal-email"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-400">Password</label>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  className="bg-[#1a1a1a] border-white/10 text-white placeholder-gray-600 rounded-xl h-11 pr-10 focus:border-blue-500"
                  data-testid="admin-portal-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium"
              data-testid="admin-portal-submit"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? 'Authenticating...' : 'Access Admin Panel'}
            </Button>
          </form>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-gray-600">Authorized personnel only. All access is logged.</p>
          <a href="/" className="text-xs text-gray-500 hover:text-blue-400 transition-colors" data-testid="admin-portal-back-link">
            &larr; Back to main site
          </a>
        </div>
      </div>
    </div>
  );
}
